import { Link } from "react-router-dom";
import Seo, { buildLocalBusinessSchema } from "@/components/Seo";
import { servicesOptions } from "@/data/services";
import { zones } from "@/data/zones";
import NotFound from "@/pages/NotFound";

const LocalPage = ({ zone }) => {
  if (!zone) {
    return <NotFound />;
  }

  const schema = buildLocalBusinessSchema({ city: zone.name });
  const otherZones = zones.filter((item) => item.slug !== zone.slug).slice(0, 6);
  const localServices = servicesOptions.slice(0, 6);

  const faqs = [
    {
      id: "delais-urgence",
      q: "Quels délais pour une urgence ?",
      a: "Nos équipes interviennent rapidement selon la priorité des urgences à Maisons-Alfort et alentours.",
    },
    {
      id: "intervention-commerce",
      q: "Intervenez-vous sur les commerces ?",
      a: "Oui, nous sécurisons rideaux métalliques, vitrines et enseignes pour les professionnels.",
    },
    {
      id: "devis-gratuit",
      q: "Proposez-vous des devis gratuits ?",
      a: "Nous établissons un devis rapide et clair pour chaque demande.",
    },
  ];

  return (
    <div data-testid={`local-page-${zone.slug}`}>
      <Seo
        title={`Dépannage alu/métal à ${zone.name} | Fast Alu`}
        description={`Intervention rapide à ${zone.name}. Dépannage alu/métal, rideaux métalliques, vitrages et automatismes 24h/24.`}
        schema={schema}
      />
      <section className="bg-brand-blue/5" data-testid="local-hero">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-red" data-testid="local-hero-kicker">
              Zone locale
            </p>
            <h1 className="font-heading text-4xl uppercase" data-testid="local-hero-title">
              Dépannage & travaux alu/métal à {zone.name} {zone.postalCode ? `(${zone.postalCode})` : ""}
            </h1>
            <p className="text-brand-ink/70 mt-4" data-testid="local-hero-text">
              {zone.description} Notre équipe intervient sur place pour les urgences, la sécurisation et les projets
              aluminium.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:0184160307"
                className="bg-brand-red text-white px-5 py-3 rounded-sm uppercase text-xs font-bold text-center"
                data-testid="local-hero-call"
              >
                Appelez 01 84 16 03 07
              </a>
              <Link
                to="/contact"
                className="bg-brand-blue text-white px-5 py-3 rounded-sm uppercase text-xs font-bold text-center"
                data-testid="local-hero-quote"
              >
                Demander un devis
              </Link>
            </div>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1759244566797-62424c733e4f"
              alt={`Intervention à ${zone.name}`}
              className="w-full h-64 object-cover rounded-sm"
              data-testid="local-hero-image"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12" data-testid="local-intro">
        <div className="bg-white border border-brand-blue/15 rounded-sm p-8">
          <h2 className="font-heading text-3xl uppercase" data-testid="local-intro-title">
            Artisan local à {zone.name}
          </h2>
          <p className="text-brand-ink/70 mt-3" data-testid="local-intro-text">
            À {zone.name}, Fast Alu assure le dépannage et les travaux alu/métal avec une approche artisanale et une
            réactivité maximale. Nous intervenons rapidement sur les urgences et planifions vos projets de
            sécurisation ou d’aménagement.
          </p>
          <p className="text-brand-ink/70 mt-3" data-testid="local-intro-text-2">
            Nos interventions à {zone.name} couvrent rideaux métalliques, serrureries, portails, façades aluminium et
            automatismes. Chaque demande est traitée avec un devis transparent et un suivi clair.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/services"
              className="border border-brand-blue/40 text-brand-blue px-4 py-2 rounded-full text-xs uppercase font-semibold"
              data-testid="local-intro-services-link"
            >
              Voir nos services
            </Link>
            <Link
              to="/contact"
              className="bg-brand-red text-white px-4 py-2 rounded-full text-xs uppercase font-semibold"
              data-testid="local-intro-contact-link"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="local-services">
        <h2 className="font-heading text-3xl uppercase" data-testid="local-services-title">
          Services clés à {zone.name}
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {localServices.map((service) => (
            <div
              key={service.slug}
              className="border border-brand-blue/15 p-6 rounded-sm bg-white"
              data-testid={`local-service-${service.slug}`}
            >
              <h3 className="font-heading text-xl uppercase" data-testid={`local-service-title-${service.slug}`}>
                {service.title}
              </h3>
              <p className="text-sm text-brand-ink/70 mt-2" data-testid={`local-service-desc-${service.slug}`}>
                Intervention locale rapide, conseils sur-mesure et finitions soignées.
              </p>
              <div className="mt-4 flex gap-3">
                <Link
                  to={`/services/${service.slug}`}
                  className="text-xs uppercase font-semibold text-brand-blue"
                  data-testid={`local-service-link-${service.slug}`}
                >
                  Voir service
                </Link>
                <Link
                  to="/contact"
                  className="text-xs uppercase font-semibold text-brand-red"
                  data-testid={`local-service-quote-${service.slug}`}
                >
                  Devis
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-blue/5" data-testid="local-faq">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl uppercase" data-testid="local-faq-title">
            Questions fréquentes
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="border border-brand-blue/15 p-6 rounded-sm bg-white"
                data-testid={`local-faq-${faq.id}`}
              >
                <p className="font-semibold" data-testid={`local-faq-question-${faq.id}`}>
                  {faq.q}
                </p>
                <p className="text-sm text-brand-ink/70 mt-2" data-testid={`local-faq-answer-${faq.id}`}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="local-other-zones">
        <h2 className="font-heading text-3xl uppercase" data-testid="local-other-zones-title">
          Zones proches
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {otherZones.map((item) => (
            <Link
              key={item.slug}
              to={`/${item.slug}`}
              className="border border-brand-blue/15 p-4 rounded-sm bg-white hover:border-brand-red"
              data-testid={`local-other-zone-${item.slug}`}
            >
              <p className="font-semibold" data-testid={`local-other-zone-name-${item.slug}`}>
                {item.name}
              </p>
              <p className="text-sm text-brand-ink/70" data-testid={`local-other-zone-desc-${item.slug}`}>
                {item.description}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LocalPage;
